import { useState, useEffect } from 'react';
import axios from 'axios';
import HomeLayout from '../components/HomeLayout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Copy, ShieldOff, ShieldCheck, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [subInfo, setSubInfo] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'technician'
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'management') {
      fetchUsers();
      fetchSubInfo();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchSubInfo = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription-info`);
      setSubInfo(response.data);
    } catch (error) {
      // Non-fatal: warning notice just won't show
      setSubInfo(null);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/invite`, formData);
      setInviteLink(`${window.location.origin}${response.data.invite_link}`);
      toast.success('Invite created successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied to clipboard');
  };

  const handleToggleActive = async (targetUser) => {
    try {
      await axios.patch(`${API}/users/${targetUser.id}/toggle-active`);
      toast.success(targetUser.is_active ? 'User deactivated' : 'User reactivated');
      fetchUsers();
      fetchSubInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
    setConfirmAction(null);
  };

  const handleDeleteUser = async (targetUser) => {
    try {
      await axios.delete(`${API}/users/${targetUser.id}`);
      toast.success('User removed successfully');
      fetchUsers();
      fetchSubInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
    setConfirmAction(null);
  };

  if (user?.role !== 'management') {
    return (
      <HomeLayout title="Users">
        <div className="text-center p-12">
          <p className="text-slate-600">You don't have permission to access this page</p>
        </div>
      </HomeLayout>
    );
  }

  return (
    <HomeLayout title="Users">
      <div data-testid="users-page" className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Users</h3>
            <p className="text-slate-600">Invite and manage user accounts</p>
          </div>
          <Dialog open={showDialog} onOpenChange={(open) => {
            if (!open) {
              setInviteLink('');
              setFormData({ email: '', name: '', role: 'technician' });
            }
            setShowDialog(open);
          }}>
            <DialogTrigger asChild>
              <Button onClick={() => setShowDialog(true)} data-testid="invite-user-button" className="bg-slate-900 hover:bg-slate-800">
                <UserPlus size={20} className="mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite New User</DialogTitle>
                <DialogDescription>
                  Generate an invite link to add a teammate to your organization.
                </DialogDescription>
              </DialogHeader>
              {!inviteLink ? (
                <form onSubmit={handleInvite} className="space-y-4" data-testid="invite-user-form">
                  {subInfo?.has_active_subscription && (
                    <div
                      data-testid="invite-billing-notice"
                      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                    >
                      <p className="font-semibold mb-1">Heads up — billing impact</p>
                      <p>
                        Inviting a new user will increase your monthly subscription by{' '}
                        <span className="font-bold">${subInfo.next_billing_increase.toFixed(2)}/month</span>.
                        The change will appear on your next billing cycle.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="management">Management</SelectItem>
                        <SelectItem value="foreman">Foreman</SelectItem>
                        <SelectItem value="technician">Technician (Shop)</SelectItem>
                        <SelectItem value="field_tech">Field Tech</SelectItem>
                        <SelectItem value="field_coordinator">Field Coordinator</SelectItem>
                        <SelectItem value="field_management">Field Management</SelectItem>
                        <SelectItem value="admin_safety">Admin / Safety</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-invite-button">
                    Generate Invite Link
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Invite Link</Label>
                    <div className="flex gap-2">
                      <Input value={inviteLink} readOnly data-testid="invite-link-display" />
                      <Button onClick={copyToClipboard} data-testid="copy-invite-link">
                        <Copy size={20} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Share this link with the user. It will expire in 7 days.
                  </p>
                  <Button onClick={() => setShowDialog(false)} className="w-full" data-testid="close-dialog-button">
                    Done
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((u) => {
                  const isSelf = u.id === user.id;
                  return (
                    <tr key={u.id} className={`hover:bg-slate-50 ${!u.is_active ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {u.name}
                        {isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                          {u.role.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold ${
                          u.is_active ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'
                        }`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!isSelf && (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'toggle', user: u })}
                              className={u.is_active
                                ? 'border-amber-300 text-amber-700 hover:bg-amber-50'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                              }
                              data-testid={`toggle-active-${u.id}`}
                            >
                              {u.is_active
                                ? <><ShieldOff size={14} className="mr-1" /> Deactivate</>
                                : <><ShieldCheck size={14} className="mr-1" /> Reactivate</>
                              }
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'delete', user: u })}
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={14} className="mr-1" /> Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="p-12 text-center text-slate-600">
                No users found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'toggle'
                ? (confirmAction.user.is_active ? 'Deactivate User' : 'Reactivate User')
                : 'Remove User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'toggle'
                ? (confirmAction.user.is_active
                  ? `Are you sure you want to deactivate ${confirmAction.user.name}? They will no longer be able to log in.`
                  : `Are you sure you want to reactivate ${confirmAction.user.name}? They will be able to log in again.`)
                : `Are you sure you want to permanently remove ${confirmAction?.user?.name}? This action cannot be undone. All data they contributed to jobs will remain — notes, time assignments, photos, PDFs, and form submissions will still show their name.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-action"
              onClick={() => {
                if (confirmAction?.type === 'toggle') {
                  handleToggleActive(confirmAction.user);
                } else {
                  handleDeleteUser(confirmAction.user);
                }
              }}
              className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmAction?.type === 'toggle'
                ? (confirmAction?.user?.is_active ? 'Deactivate' : 'Reactivate')
                : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </HomeLayout>
  );
}
